import jsPDF from 'jspdf';
import { NotoSansHebrewFont } from './noto-sans-hebrew-font';
import { NotoSansHebrewBoldFont } from './noto-sans-hebrew-bold-font';

// Format date to Hebrew locale
export const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    // Reverse each component for RTL rendering
    return `${year.split('').reverse().join('')}.${month.split('').reverse().join('')}.${day.split('').reverse().join('')}`;
};

// Format price to ILS currency
export const formatPrice = (price) => {
    // Format without comma separator: "1800.00"
    const formatted = Number(price).toFixed(2);

    // Reverse entire string for RTL: "1800.00" becomes "00.0081"
    const reversed = formatted.split('').reverse().join('');

    return `₪ ${reversed}`;
};


// Reverse numbers in a string for RTL rendering
export const reverseNumbers = (str) => {
    if (!str || str === '-') return str;
    return str.split('').reverse().join('');
};

/**
 * Create a jsPDF document pre-configured with Hebrew fonts and RTL support
 * @returns {jsPDF} Configured jsPDF document
 */
export const createHebrewPDF = () => {
    const doc = new jsPDF();
    doc.addFileToVFS('NotoSansHebrew.ttf', NotoSansHebrewFont);
    doc.addFont('NotoSansHebrew.ttf', 'NotoSansHebrew', 'normal');
    doc.addFileToVFS('NotoSansHebrew-Bold.ttf', NotoSansHebrewBoldFont);
    doc.addFont('NotoSansHebrew-Bold.ttf', 'NotoSansHebrew', 'bold');
    doc.setFont('NotoSansHebrew', 'bold');
    doc.setR2L(true);
    doc.setTextColor(0, 0, 0);
    return doc;
};

/**
 * Generate an invoice report PDF with Hebrew support
 * @param {Array} invoices - Array of invoice objects
 * @param {string} startDate - Start date of the report range
 * @param {string} endDate - End date of the report range
 * @param {string} businessName - Name of the business
 * @returns {Promise<Blob>} - PDF blob
 */
export const generateInvoiceReportPDF = async (invoices, startDate, endDate, businessName) => {
    const doc = createHebrewPDF();

    // Calculate total
    const total = invoices.reduce((sum, inv) => sum + (inv.price || 0), 0);

    // Page settings
    const pageWidth = doc.internal.pageSize.width;
    const margin = 15;
    let yPos = 20;

    // Add title
    doc.setFontSize(20);
    doc.text(businessName, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    doc.setFontSize(16);
    doc.text('דוח חשבוניות', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    doc.setFontSize(12);
    doc.text(`${formatDate(startDate)} - ${formatDate(endDate)}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    // Add summary box
    doc.setFillColor(249, 245, 255); // Light purple background
    doc.rect(margin, yPos, pageWidth - 2 * margin, 20, 'F');

    doc.setFontSize(14);
    doc.text(formatPrice(total), pageWidth - margin - 5, yPos + 8, { align: 'right' });
    doc.setFontSize(11);
    doc.text(`מספר חשבוניות: ${invoices.length}`, pageWidth - margin - 5, yPos + 15, { align: 'right' });
    yPos += 28;

    // Table settings
    const tableStartY = yPos;
    const rowHeight = 8;
    const headerHeight = 10;

    // Column widths and positions (from right to left)
    const col1Width = 30; // Invoice Number
    const col2Width = 35; // Date
    const col3Width = 40; // Customer
    const col4Width = 50; // Services
    const col5Width = 30; // Price

    const col5X = pageWidth - margin; // Price (rightmost)
    const col4X = col5X - col5Width; // Services
    const col3X = col4X - col4Width; // Customer
    const col2X = col3X - col3Width; // Date
    const col1X = col2X - col1Width; // Invoice Number (leftmost)

    // Draw table header
    doc.setFillColor(102, 51, 153); // Purple
    doc.rect(margin, yPos, pageWidth - 2 * margin, headerHeight, 'F');

    doc.setTextColor(255, 255, 255); // White text
    doc.setFontSize(10);
    doc.text('מחיר', col5X - col5Width / 2, yPos + 7, { align: 'center' });
    doc.text('שירותים', col4X - col4Width / 2, yPos + 7, { align: 'center' });
    doc.text('לקוח', col3X - col3Width / 2, yPos + 7, { align: 'center' });
    doc.text('תאריך', col2X - col2Width / 2, yPos + 7, { align: 'center' });
    doc.text('מס\' חשבונית', col1X - col1Width / 2, yPos + 7, { align: 'center' });

    yPos += headerHeight;
    doc.setFont('NotoSansHebrew', 'bold');
    doc.setTextColor(0, 0, 0); // Black text for body

    // Draw table rows
    doc.setFontSize(9);
    invoices.forEach((invoice, index) => {
        // Alternate row colors
        if (index % 2 === 0) {
            doc.setFillColor(249, 249, 249);
            doc.rect(margin, yPos, pageWidth - 2 * margin, rowHeight, 'F');
        }

        // Add new page if needed
        if (yPos > 270) {
            doc.addPage();
            yPos = 20;

            // Redraw header on new page
            doc.setFillColor(102, 51, 153);
            doc.rect(margin, yPos, pageWidth - 2 * margin, headerHeight, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.text('מחיר', col5X - col5Width / 2, yPos + 7, { align: 'center' });
            doc.text('שירותים', col4X - col4Width / 2, yPos + 7, { align: 'center' });
            doc.text('לקוח', col3X - col3Width / 2, yPos + 7, { align: 'center' });
            doc.text('תאריך', col2X - col2Width / 2, yPos + 7, { align: 'center' });
            doc.text('מס\' חשבונית', col1X - col1Width / 2, yPos + 7, { align: 'center' });
            yPos += headerHeight;
            doc.setFont('NotoSansHebrew', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(9);
        }

        // Truncate long text to fit in columns
        const truncate = (text, maxWidth) => {
            const textWidth = doc.getTextWidth(text);
            if (textWidth <= maxWidth - 4) return text;

            let truncated = text;
            while (doc.getTextWidth(truncated + '...') > maxWidth - 4 && truncated.length > 0) {
                truncated = truncated.slice(0, -1);
            }
            return truncated + '...';
        };

        // Draw cell content
        const cellY = yPos + 6;
        doc.text(formatPrice(invoice.price || 0), col5X - col5Width / 2, cellY, { align: 'center' });
        doc.text(truncate(invoice.serviceNames || '', col4Width), col4X - col4Width / 2, cellY, { align: 'center' });
        doc.text(truncate(invoice.customerName || '', col3Width), col3X - col3Width / 2, cellY, { align: 'center' });
        doc.text(formatDate(invoice.invoiceCreatedAt), col2X - col2Width / 2, cellY, { align: 'center' });
        doc.text(reverseNumbers(invoice.invoiceNumber || '-'), col1X - col1Width / 2, cellY, { align: 'center' });

        yPos += rowHeight;
    });

    // Draw table border
    doc.setDrawColor(200, 200, 200);
    doc.rect(margin, tableStartY, pageWidth - 2 * margin, yPos - tableStartY);

    const blob = doc.output('blob');
    return blob;
};

export default generateInvoiceReportPDF;
